import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Download, Phone, Mail, MapPin, Package, Truck, Target, Shield, Leaf, Star, ArrowRight, Menu, X, Instagram } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import mockData from '../mock/mockData';

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    phone: '',
    email: '',
    segment: '',
    message: ''
  });

  const slides = mockData.heroSlides;
  const products = mockData.products;
  const differentials = mockData.differentials;
  const testimonials = mockData.testimonials;
  const portfolio = mockData.portfolio;

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [slides.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const whatsappMessage = `Olá! Gostaria de um orçamento.\n\nNome: ${formData.name}\nEmpresa: ${formData.company}\nTelefone: ${formData.phone}\nEmail: ${formData.email}\nSegmento: ${formData.segment}\nMensagem: ${formData.message}`;
    const whatsappUrl = `https://wa.me/5541995291849?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
    toast.success('Redirecionando para o WhatsApp!');
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileMenuOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* WhatsApp Floating Button */}
      <a
        href="https://wa.me/5541995291849?text=Olá!%20Gostaria%20de%20saber%20mais%20sobre%20as%20embalagens%20da%20JZ."
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-green-500 hover:bg-green-600 text-white rounded-full p-4 shadow-2xl transition-all duration-300 hover:scale-110 group animate-pulse-custom"
        aria-label="Falar no WhatsApp"
      >
        <svg 
          viewBox="0 0 24 24" 
          className="w-8 h-8 fill-current"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
        </svg>
        <span className="absolute right-full mr-3 top-1/2 -translate-y-1/2 bg-gray-900 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
          Fale conosco no WhatsApp
        </span>
      </a>

      {/* Header */}
      <header className="fixed top-0 w-full bg-white/95 backdrop-blur-sm shadow-sm z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <img
                src="logo.jpeg"
                alt="JZ Embalagens"
                className="h-12 w-auto" />

            </div>
            
            <nav className="hidden lg:flex items-center space-x-8">
              <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-blue-900 transition-colors font-medium">Início</button>
              <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-900 transition-colors font-medium">Quem Somos</button>
              <button onClick={() => scrollToSection('products')} className="text-gray-700 hover:text-blue-900 transition-colors font-medium">Produtos</button>
              <button onClick={() => scrollToSection('portfolio')} className="text-gray-700 hover:text-blue-900 transition-colors font-medium">Portfólio</button>
              <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-900 transition-colors font-medium">Contato</button>
              <a href="apresentacao_jz_embalagens.pdf" target="_blank" rel="noopener noreferrer">
                <Button className="bg-blue-900 hover:bg-blue-800 text-white">
                  <Download className="w-4 h-4 mr-2" />
                  Baixar Catálogo
                </Button>
              </a>
            </nav>

            <button
              className="lg:hidden text-gray-700"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>

              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen &&
          <div className="lg:hidden mt-4 pb-4 border-t pt-4">
              <nav className="flex flex-col space-y-3">
                <button onClick={() => scrollToSection('home')} className="text-gray-700 hover:text-blue-900 transition-colors font-medium text-left">Início</button>
                <button onClick={() => scrollToSection('about')} className="text-gray-700 hover:text-blue-900 transition-colors font-medium text-left">Quem Somos</button>
                <button onClick={() => scrollToSection('products')} className="text-gray-700 hover:text-blue-900 transition-colors font-medium text-left">Produtos</button>
                <button onClick={() => scrollToSection('portfolio')} className="text-gray-700 hover:text-blue-900 transition-colors font-medium text-left">Portfólio</button>
                <button onClick={() => scrollToSection('contact')} className="text-gray-700 hover:text-blue-900 transition-colors font-medium text-left">Contato</button>
                <a href="apresentacao_jz_embalagens.pdf" target="_blank" rel="noopener noreferrer">
                  <Button className="bg-blue-900 hover:bg-blue-800 text-white w-full">
                    <Download className="w-4 h-4 mr-2" />
                    Baixar Catálogo
                  </Button>
                </a>
              </nav>
            </div>
          }
        </div>
      </header>

      {/* Hero Slideshow */}
      <section id="home" className="relative h-screen mt-16">
        <div className="absolute inset-0 overflow-hidden">
          {slides.map((slide, index) =>
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-1000 ${
            index === currentSlide ? 'opacity-100' : 'opacity-0'}`
            }>

              <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${slide.image})` }}>

                <div className="absolute inset-0 bg-gradient-to-r from-blue-950/90 via-blue-900/70 to-transparent" />
              </div>
              <div className="relative h-full container mx-auto px-4 flex items-center">
                <div className="max-w-2xl text-white space-y-6 animate-fadeIn">
                  <h1 className="text-5xl lg:text-6xl font-bold leading-tight">{slide.title}</h1>
                  <p className="text-xl lg:text-2xl text-blue-100">{slide.subtitle}</p>
                  <p className="text-lg text-gray-200 leading-relaxed">{slide.description}</p>
                  <div className="flex flex-wrap gap-4 pt-4">
                    <Button
                    onClick={() => scrollToSection('contact')}
                    className="bg-white text-blue-900 hover:bg-gray-100 text-lg px-8 py-6">

                      Solicitar Orçamento
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </Button>
                    <a href="apresentacao_jz_embalagens.pdf" target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-blue-900 text-lg px-8 py-6">
                        <Download className="w-5 h-5 mr-2" />
                        Ver Catálogo
                      </Button>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all">

          <ChevronLeft className="w-6 h-6" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white p-3 rounded-full transition-all">

          <ChevronRight className="w-6 h-6" />
        </button>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex space-x-2">
          {slides.map((_, index) =>
          <button
            key={index}
            onClick={() => setCurrentSlide(index)}
            className={`w-3 h-3 rounded-full transition-all ${
            index === currentSlide ? 'bg-white w-8' : 'bg-white/50'}`
            } />

          )}
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="lg:text-5xl !font-bold !text-4xl mb-4 text-blue-900">MUITO MAIS QUE UMA FÁBRICA DE EMBALAGENS.

            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Somos solução, inovação e entrega eficiente.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-6">
              <p className="text-lg text-gray-700 leading-relaxed">
                Na JZ Embalagens, nós não vendemos apenas sacolas ou embalagens. Entregamos experiências personalizadas que fortalecem marcas, valorizam produtos e encantam consumidores.
              </p>
              <p className="!text-lg text-gray-700">Com mais de 1 Milhão de embalagens produzidas e entregues, e mais de 800 Clientes satisfeitos em todo o Brasil, somos referência em soluções personalizadas para os mais diversos segmentos: restaurantes, hamburguerias, lojas de roupas, padarias, eventos, indústria e muito mais.

              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Nossa produção é ágil, flexível e focada em resultados. Atendemos desde grandes redes até pequenos empreendedores com o mesmo cuidado e padrão de qualidade.
              </p>
            </div>
            <div className="relative h-96 rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="https://customer-assets.emergentagent.com/job_jz-embalagens/artifacts/gbicn023_foto%20lateral%20descritivo%202.png"
                alt="JZ Embalagens Fábrica"
                className="w-full h-full object-cover" />

            </div>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <Card className="text-center p-6 hover:shadow-lg transition-shadow border-t-4 border-blue-900">
              <CardContent className="pt-6">
                <div className="text-4xl font-bold text-blue-900 mb-2">+1 Milhão</div>
                <p className="text-gray-600">Embalagens Produzidas</p>
              </CardContent>
            </Card>
            <Card className="text-center p-6 hover:shadow-lg transition-shadow border-t-4 border-blue-900">
              <CardContent className="pt-6">
                <div className="text-4xl font-bold text-blue-900 mb-2">+800</div>
                <p className="text-gray-600">Empresas Atendidas</p>
              </CardContent>
            </Card>
            <Card className="text-center p-6 hover:shadow-lg transition-shadow border-t-4 border-blue-900">
              <CardContent className="pt-6">
                <div className="text-4xl font-bold text-blue-900 mb-2">Express</div>
                <p className="text-gray-600">Entrega RMC</p>
              </CardContent>
            </Card>
            <Card className="text-center p-6 hover:shadow-lg transition-shadow border-t-4 border-blue-900">
              <CardContent className="pt-6">
                <div className="text-4xl font-bold text-blue-900 mb-2">Nacional</div>
                <p className="text-gray-600">Envio para todo o Brasil</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-blue-900 mb-4">
              Sua embalagem fala muito sobre sua marca
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Nossa missão é fazer ela falar bem alto.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {products.map((product, index) =>
            <Card key={index} className="group hover:shadow-2xl transition-all duration-300 overflow-hidden">
                <div className="h-48 bg-gradient-to-br from-blue-50 to-gray-100 flex items-center justify-center group-hover:scale-105 transition-transform duration-300">
                  <div className="text-blue-900">{product.icon}</div>
                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-blue-900 mb-3">{product.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{product.description}</p>
                </CardContent>
              </Card>
            )}
          </div>

          <div className="mt-12 p-8 bg-blue-50 rounded-2xl border-l-4 border-blue-900">
            <h3 className="text-2xl font-bold text-blue-900 mb-4">Modelos sob medida ou soluções prontas?</h3>
            <p className="text-gray-700 leading-relaxed">
              Você escolhe. Temos modelos exclusivos com design padronizado prontos para entrega imediata, com valores acessíveis e baixo custo de personalização. Perfeito para quem quer economia sem abrir mão da identidade visual.
            </p>
          </div>
        </div>
      </section>

      {/* Differentials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-blue-900 mb-4">
              Por que escolher a JZ Embalagens?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Entregamos muito mais do que pacotes. Entregamos valor, confiança e presença.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {differentials.map((item, index) =>
            <Card key={index} className="p-6 hover:shadow-xl transition-all border-t-4 border-blue-900">
                <CardContent className="pt-6 space-y-4">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-900">
                    {item.icon}
                  </div>
                  <h3 className="text-xl font-bold text-blue-900">{item.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{item.description}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      <section id="portfolio" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-blue-900 mb-4">
              Parcerias que inspiram confiança
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Marcas que acreditam na qualidade da JZ Embalagens
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {portfolio.map((item, index) =>
            <Card key={index} className="group overflow-hidden hover:shadow-2xl transition-all">
                <div className="h-64 overflow-hidden">
                  <img
                  src={item.image}
                  alt={item.client}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                </div>
                <CardContent className="p-6">
                  <h3 className="text-xl font-bold text-blue-900 mb-2">{item.client}</h3>
                  <p className="text-sm text-gray-500 mb-3">{item.segment}</p>
                  <p className="text-gray-600">{item.description}</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-blue-900 mb-4">
              A voz de quem confia na gente
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Clientes que embalam suas marcas com a JZ
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) =>
            <Card key={index} className="p-6 hover:shadow-xl transition-all">
                <CardContent className="pt-6 space-y-4">
                  <div className="flex items-center space-x-4">
                    <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center text-blue-900 font-bold text-xl">
                      {testimonial.name.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-bold text-blue-900">{testimonial.name}</h4>
                      <p className="text-sm text-gray-500">{testimonial.segment}</p>
                    </div>
                  </div>
                  <div className="flex space-x-1">
                    {[...Array(5)].map((_, i) =>
                  <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                  )}
                  </div>
                  <p className="text-gray-600 italic leading-relaxed">"{testimonial.testimonial}"</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-blue-900 mb-4">
              Vamos criar sua embalagem ideal?
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Preencha os campos abaixo e fale com nossa equipe
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            <Card className="p-8">
              <CardContent className="pt-6">
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nome Completo *</label>
                    <Input
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      placeholder="Seu nome" />

                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Nome da Empresa *</label>
                    <Input
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      required
                      placeholder="Nome da sua empresa" />

                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Telefone / WhatsApp *</label>
                    <Input
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      placeholder="+55 41 99529-1849" />

                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">E-mail *</label>
                    <Input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      placeholder="seu@email.com" />

                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Segmento de Atuação</label>
                    <Input
                      name="segment"
                      value={formData.segment}
                      onChange={handleInputChange}
                      placeholder="Ex: Restaurante, Loja de Roupas" />

                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Descreva sua necessidade</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder="Conte-nos o que você precisa..."
                      rows={4} />

                  </div>
                  <Button type="submit" className="w-full bg-blue-900 hover:bg-blue-800 text-white py-6 text-lg">
                    Quero um orçamento personalizado
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                  <a href="apresentacao_jz_embalagens.pdf" target="_blank" rel="noopener noreferrer" className="block">
                    <Button type="button" variant="outline" className="w-full border-2 border-blue-900 text-blue-900 hover:bg-blue-50 py-6 text-lg">
                      <Download className="w-5 h-5 mr-2" />
                      Baixar Catálogo Técnico
                    </Button>
                  </a>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-8">
              <Card className="p-8">
                <CardContent className="pt-6 space-y-6">
                  <div className="flex items-start space-x-4">
                    <Phone className="w-6 h-6 text-blue-900 mt-1" />
                    <div>
                      <h3 className="font-bold text-blue-900 mb-1">Telefone / WhatsApp</h3>
                      <a href="https://wa.me/5541995291849" className="text-gray-600 hover:text-blue-900 transition-colors">
                        +55 41 99529-1849
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <Mail className="w-6 h-6 text-blue-900 mt-1" />
                    <div>
                      <h3 className="font-bold text-blue-900 mb-1">E-mail</h3>
                      <a href="mailto:jzembalagens@gmail.com" className="text-gray-600 hover:text-blue-900 transition-colors">
                        jzembalagens@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start space-x-4">
                    <MapPin className="w-6 h-6 text-blue-900 mt-1" />
                    <div>
                      <h3 className="font-bold text-blue-900 mb-1">Endereço</h3>
                      <p className="text-gray-600">
                        R. Aluísio de Azevedo, 248<br />
                        Vargem Grande, Pinhais - PR<br />
                        CEP: 83321-270
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <div className="h-80 rounded-2xl overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3603.7397!2d-49.1913!3d-25.4447!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x94dce3f!2sR.%20Alu%C3%ADsio%20de%20Azevedo%2C%20248%20-%20Vargem%20Grande%2C%20Pinhais%20-%20PR%2C%2083321-270!5e0!3m2!1spt-BR!2sbr!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Localização JZ Embalagens" />

              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-blue-950 text-white py-12">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img
                src="logo_rodape.png"
                alt="JZ Embalagens"
                className="h-12  mb-4" />

              <p className="text-gray-300 text-sm">
                Soluções em embalagens personalizadas para todo o Brasil
              </p>
              <div className="mt-4 flex items-center gap-3">
                <a
                  href="https://www.instagram.com/jz_embalagens?igsh=MnhmMG91bHN4Nzcx"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
                  aria-label="Instagram da JZ Embalagens"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4">Menu</h4>
              <nav className="space-y-2 text-sm">
                <button onClick={() => scrollToSection('home')} className="block text-gray-300 hover:text-white transition-colors">Início</button>
                <button onClick={() => scrollToSection('about')} className="block text-gray-300 hover:text-white transition-colors">Quem Somos</button>
                <button onClick={() => scrollToSection('products')} className="block text-gray-300 hover:text-white transition-colors">Produtos</button>
                <button onClick={() => scrollToSection('contact')} className="block text-gray-300 hover:text-white transition-colors">Contato</button>
              </nav>
            </div>
            <div>
              <h4 className="font-bold mb-4">Contato</h4>
              <div className="space-y-2 text-sm text-gray-300">
                <p>+55 41 99529-1849</p>
                <p>jzembalagens@gmail.com</p>
                <p>Pinhais - PR</p>
              </div>
            </div>
            <div>
              <h4 className="font-bold mb-4">Horário de Atendimento</h4>
              <div className="space-y-2 text-sm text-gray-300">
                <p>Segunda a Sexta</p>
                <p>08:00 - 18:00</p>
                <p>Sábado: 08:00 - 12:00</p>
              </div>
            </div>
          </div>
          <div className="border-t border-gray-700 pt-8 text-center text-sm text-gray-400">
            <p>&copy; 2025 JZ Embalagens. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
      <style>{`#emergent-badge { display: none !important; }`}</style>
    </div>);

};

export default Home;